/**
 * CodeEditorIDE.js
 * IDE multi-fichiers basé sur Monaco Editor avec zones verrouillées et linting Python.
 */
import React from "react";
import Editor from "@monaco-editor/react";
import {Box, Chip, IconButton, Paper, Stack, TextField, Tooltip, Typography} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {lintPython, isPyodideReady, preloadPyodide} from "./PythonLinter";

// ── Helpers zones verrouillées ─────────────────────────────────────────────
function computeLockRanges(content) {
    const ranges = [];
    const lines = content.split("\n");
    let inLock = false, lockStart = -1;
    lines.forEach((line, i) => {
        if (line.includes("LOCK:") && line.includes(":START")) { inLock = true; lockStart = i; }
        if (line.includes("LOCK:") && line.includes(":END")) { if (inLock) ranges.push({start: lockStart, end: i}); inLock = false; }
    });
    return ranges;
}

// ── Composant principal ────────────────────────────────────────────────────
export default function CodeEditorIDE({files, setFiles, language, readOnlyFiles = {}}) {
    const [activeFile, setActiveFile] = React.useState(Object.keys(files)[0] || "handler.py");
    const [newFileName, setNewFileName] = React.useState("");
    const [adding, setAdding] = React.useState(false);
    const [lintErrors, setLintErrors] = React.useState([]);
    const [linting, setLinting] = React.useState(false);
    const editorRef = React.useRef(null);
    const monacoRef = React.useRef(null);
    const decorationsRef = React.useRef([]);

    const fileNames = Object.keys(files);
    const readOnlyFileNames = Object.keys(readOnlyFiles);
    const isReadOnly = readOnlyFileNames.includes(activeFile);
    const content = isReadOnly ? (readOnlyFiles[activeFile] ?? "") : (files[activeFile] ?? "");

    // Précharger Pyodide en arrière-plan
    React.useEffect(() => { preloadPyodide(); }, []);

    // Lint debounced (1s)
    React.useEffect(() => {
        if (language !== "PYTHON" || !activeFile.endsWith(".py")) {
            setLintErrors([]);
            return;
        }
        setLinting(true);
        const timer = setTimeout(async () => {
            const errs = await lintPython(content);
            setLintErrors(errs);
            setLinting(false);
        }, 1000);
        return () => clearTimeout(timer);
    }, [content, language, activeFile]);

    // Appliquer les markers Monaco depuis les erreurs de lint
    React.useEffect(() => {
        const monaco = monacoRef.current;
        const editor = editorRef.current;
        if (!monaco || !editor) return;
        const model = editor.getModel();
        if (!model) return;

        const markers = lintErrors.map(err => ({
            severity: err.severity === "error"
                ? monaco.MarkerSeverity.Error
                : monaco.MarkerSeverity.Warning,
            startLineNumber: err.line,
            startColumn: err.col || 1,
            endLineNumber: err.line,
            endColumn: model.getLineMaxColumn(err.line),
            message: err.msg,
            source: "pyodide-linter",
        }));
        monaco.editor.setModelMarkers(model, "pyodide-linter", markers);
    }, [lintErrors]);

    // Appliquer les décorations de zones verrouillées
    const applyLockDecorations = React.useCallback((editor, monaco, text) => {
        const lockRanges = computeLockRanges(text);
        const newDecorations = lockRanges.map(r => ({
            range: new monaco.Range(r.start + 1, 1, r.end + 1, 1),
            options: {
                isWholeLine: true,
                className: "locked-zone-line",
                glyphMarginClassName: "locked-zone-glyph",
                linesDecorationsClassName: "locked-zone-border",
            },
        }));
        decorationsRef.current = editor.deltaDecorations(decorationsRef.current, newDecorations);
    }, []);

    // Protéger les zones LOCK contre l'édition
    const setupLockProtection = React.useCallback((editor, monaco) => {
        editor.onDidChangeModelContent((e) => {
            // Vérifié après chaque frappe : le contenu actuel a-t-il encore toutes ses zones LOCK intactes ?
            // On délègue la protection au onDidAttemptReadOnlyEdit ci-dessous.
        });
    }, []);

    function handleEditorDidMount(editor, monaco) {
        editorRef.current = editor;
        monacoRef.current = monaco;

        // Définir le thème custom
        monaco.editor.defineTheme("promise-dark", {
            base: "vs-dark",
            inherit: true,
            rules: [],
            colors: {
                "editor.background": "#1e1e1e",
                "editorGutter.background": "#252526",
            },
        });
        monaco.editor.setTheme("promise-dark");

        // Ajouter CSS pour les zones verrouillées
        const style = document.getElementById("monaco-lock-styles") || document.createElement("style");
        style.id = "monaco-lock-styles";
        style.textContent = `
            .locked-zone-line { background-color: rgba(229, 57, 53, 0.08) !important; }
            .locked-zone-border { background-color: #e53935; width: 3px !important; margin-left: 3px; }
            .locked-zone-glyph { }
        `;
        if (!document.getElementById("monaco-lock-styles")) document.head.appendChild(style);

        applyLockDecorations(editor, monaco, content);
        setupLockProtection(editor, monaco);

        // Enregistrer la complétion pour Python
        if (!monaco.__promiseCompletionRegistered) {
            monaco.__promiseCompletionRegistered = true;
            registerPythonCompletion(monaco);
            registerRCompletion(monaco);
        }
    }

    function handleEditorChange(value) {
        if (isReadOnly || !value) return;

        // Vérifier que les zones LOCK n'ont pas été modifiées
        const oldLocks = computeLockRanges(content);
        const oldLines = content.split("\n");
        const newLines = value.split("\n");

        for (const r of oldLocks) {
            const oldLockContent = oldLines.slice(r.start, r.end + 1).join("\n");
            if (!value.includes(oldLockContent)) {
                // Revert — on remet le contenu précédent
                const editor = editorRef.current;
                if (editor) {
                    editor.setValue(content);
                    applyLockDecorations(editor, monacoRef.current, content);
                }
                return;
            }
        }

        setFiles(prev => ({...prev, [activeFile]: value}));

        // Mettre à jour les décorations
        const editor = editorRef.current;
        const monaco = monacoRef.current;
        if (editor && monaco) {
            applyLockDecorations(editor, monaco, value);
        }
    }

    // Quand on change de fichier actif, réappliquer les décorations
    React.useEffect(() => {
        const editor = editorRef.current;
        const monaco = monacoRef.current;
        if (editor && monaco) {
            setTimeout(() => applyLockDecorations(editor, monaco, content), 50);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFile]);

    function addFile() {
        if (!newFileName.trim()) return;
        setFiles(prev => ({...prev, [newFileName.trim()]: ""}));
        setActiveFile(newFileName.trim());
        setNewFileName(""); setAdding(false);
    }
    function removeFile(name) {
        if (name === "handler.py" || name === "handler.R") return;
        setFiles(prev => { const next = {...prev}; delete next[name]; return next; });
        if (activeFile === name) setActiveFile(fileNames[0]);
    }

    const ext = language === "R" ? ".R" : ".py";
    const errCount = lintErrors.filter(e => e.severity === "error").length;
    const warnCount = lintErrors.filter(e => e.severity === "warning").length;
    const lockRanges = computeLockRanges(content);

    // Déterminer le langage Monaco
    function monacoLanguage() {
        if (isReadOnly) {
            if (activeFile.endsWith(".py")) return "python";
            if (activeFile.endsWith(".R")) return "r";
        }
        if (activeFile.endsWith(".txt")) return "plaintext";
        if (activeFile.endsWith(".R")) return "r";
        if (activeFile.endsWith(".json")) return "json";
        if (activeFile.endsWith(".yaml") || activeFile.endsWith(".yml")) return "yaml";
        return "python";
    }

    return (
        <Paper variant="outlined" sx={{borderRadius: 2, overflow: "hidden", display: "flex", flexDirection: "column", flex: 1, minHeight: 300}}>
            {/* Tabs */}
            <Stack direction="row" sx={{bgcolor: "grey.900", px: 0.5, py: 0.25, gap: 0.25, flexWrap: "wrap", alignItems: "center"}}>
                {fileNames.map(name => (
                    <Chip key={name} label={name} size="small"
                        onClick={() => setActiveFile(name)}
                        onDelete={name !== `handler${ext}` && name !== "requirements.txt" ? () => removeFile(name) : undefined}
                        sx={{height: 24, fontSize: 11, fontFamily: "monospace",
                            bgcolor: activeFile === name ? "grey.700" : "transparent",
                            color: activeFile === name ? "#fff" : "grey.400",
                            "&:hover": {bgcolor: "grey.700"},
                            "& .MuiChip-deleteIcon": {fontSize: 14, color: "grey.500"}}}/>
                ))}
                {readOnlyFileNames.length > 0 && fileNames.length > 0 && (
                    <Box sx={{width: "1px", height: 16, bgcolor: "grey.700", mx: 0.5}}/>
                )}
                {readOnlyFileNames.map(name => (
                    <Tooltip key={name} title="Fichier plateforme (lecture seule)" placement="top" arrow>
                        <Chip label={`🔒 ${name}`} size="small"
                            onClick={() => setActiveFile(name)}
                            sx={{height: 24, fontSize: 11, fontFamily: "monospace",
                                bgcolor: activeFile === name ? "#37474f" : "transparent",
                                color: activeFile === name ? "#b0bec5" : "grey.500",
                                border: "1px dashed",
                                borderColor: activeFile === name ? "grey.600" : "transparent",
                                "&:hover": {bgcolor: "#37474f"}}}/>
                    </Tooltip>
                ))}
                {adding ? (
                    <TextField size="small" autoFocus value={newFileName}
                        onChange={e => setNewFileName(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") addFile(); if (e.key === "Escape") setAdding(false); }}
                        onBlur={() => { if (newFileName.trim()) addFile(); else setAdding(false); }}
                        placeholder="nom.py"
                        sx={{width: 120, "& input": {color: "#fff", fontSize: 11, py: 0.25, px: 0.5, fontFamily: "monospace"}, "& fieldset": {borderColor: "grey.600"}}}/>
                ) : (
                    <IconButton size="small" onClick={() => setAdding(true)} sx={{color: "grey.400"}}>
                        <AddIcon sx={{fontSize: 14}}/>
                    </IconButton>
                )}
            </Stack>

            {/* Monaco Editor */}
            <Box sx={{flex: 1, minHeight: 300}}>
                <Editor
                    height="100%"
                    language={monacoLanguage()}
                    value={content}
                    onChange={handleEditorChange}
                    onMount={handleEditorDidMount}
                    theme="promise-dark"
                    options={{
                        readOnly: isReadOnly,
                        minimap: {enabled: false},
                        fontSize: 13,
                        fontFamily: "'Fira Code', Consolas, Monaco, monospace",
                        fontLigatures: true,
                        lineNumbers: "on",
                        scrollBeyondLastLine: false,
                        automaticLayout: true,
                        tabSize: 4,
                        insertSpaces: true,
                        wordWrap: "off",
                        renderWhitespace: "selection",
                        bracketPairColorization: {enabled: true},
                        guides: {bracketPairs: true, indentation: true},
                        suggest: {
                            showKeywords: true,
                            showSnippets: true,
                            showFunctions: true,
                            showVariables: true,
                        },
                        quickSuggestions: {other: true, comments: false, strings: false},
                        parameterHints: {enabled: true},
                        folding: true,
                        glyphMargin: true,
                        scrollbar: {verticalScrollbarSize: 8, horizontalScrollbarSize: 8},
                    }}
                />
            </Box>

            {/* Status bar */}
            <Stack direction="row" sx={{bgcolor: "grey.900", px: 1.5, py: 0.25, gap: 1.5, borderTop: "1px solid", borderColor: "grey.800", alignItems: "center", flexShrink: 0}}>
                <Chip label={language} size="small" sx={{height: 16, fontSize: 9, bgcolor: language === "PYTHON" ? "#306998" : "#276DC3", color: "#fff"}}/>
                <Chip label={`${content.split("\n").length} lignes`} size="small" sx={{height: 16, fontSize: 9, bgcolor: "grey.800", color: "grey.400"}}/>
                <Chip label={`${lockRanges.length} lock`} size="small"
                      sx={{height: 16, fontSize: 9, bgcolor: lockRanges.length > 0 ? "#b71c1c" : "grey.800", color: lockRanges.length > 0 ? "#fff" : "grey.400"}}/>
                <Chip label={activeFile} size="small" sx={{height: 16, fontSize: 9, bgcolor: "grey.800", color: "grey.400", fontFamily: "monospace"}}/>
                {isPyodideReady() && <Chip label="Pyodide" size="small" sx={{height: 16, fontSize: 9, bgcolor: "#306998", color: "#fff"}}/>}
                {linting && <Chip label="analyse..." size="small" sx={{height: 16, fontSize: 9, bgcolor: "grey.800", color: "grey.400"}}/>}

                <Box sx={{flex: 1}}/>

                {/* Erreurs / Warnings */}
                {errCount > 0 && (
                    <Box sx={{display: "flex", alignItems: "center", gap: 0.25}}>
                        <ErrorOutlineIcon sx={{fontSize: 11, color: "#ff5252"}}/>
                        <Typography variant="caption" sx={{fontSize: 10, color: "#ff5252", fontWeight: 700}}>{errCount}</Typography>
                    </Box>
                )}
                {warnCount > 0 && (
                    <Box sx={{display: "flex", alignItems: "center", gap: 0.25}}>
                        <WarningAmberIcon sx={{fontSize: 11, color: "#ffb74d"}}/>
                        <Typography variant="caption" sx={{fontSize: 10, color: "#ffb74d", fontWeight: 700}}>{warnCount}</Typography>
                    </Box>
                )}
                {!linting && errCount === 0 && warnCount === 0 && language === "PYTHON" && activeFile.endsWith(".py") && (
                    <Chip label="OK" size="small" sx={{height: 16, fontSize: 9, bgcolor: "#1b5e20", color: "#fff"}}/>
                )}
            </Stack>
        </Paper>
    );
}

// ── Autocomplétion Python ──────────────────────────────────────────────────
function registerPythonCompletion(monaco) {
    monaco.languages.registerCompletionItemProvider("python", {
        triggerCharacters: [".", " "],
        provideCompletionItems: (model, position) => {
            const word = model.getWordUntilPosition(position);
            const range = {
                startLineNumber: position.lineNumber,
                endLineNumber: position.lineNumber,
                startColumn: word.startColumn,
                endColumn: word.endColumn,
            };

            // Ligne courante pour contexte
            const lineContent = model.getLineContent(position.lineNumber);
            const textBefore = lineContent.substring(0, position.column - 1);

            const suggestions = [];

            // Complétion après "np."
            if (textBefore.endsWith("np.")) {
                const npItems = [
                    "array", "zeros", "ones", "empty", "arange", "linspace", "logspace",
                    "mean", "std", "var", "median", "sum", "min", "max", "argmin", "argmax",
                    "abs", "sqrt", "exp", "log", "log2", "log10", "power", "square",
                    "dot", "matmul", "transpose", "reshape", "flatten", "ravel",
                    "concatenate", "stack", "vstack", "hstack", "split",
                    "where", "nonzero", "sort", "argsort", "unique",
                    "random", "nan", "inf", "pi", "isnan", "isinf", "isfinite",
                    "float64", "int64", "bool_", "ndarray",
                    "polyfit", "polyval", "corrcoef", "cov",
                    "fft", "linalg", "clip", "round", "ceil", "floor",
                ];
                npItems.forEach(item => suggestions.push({
                    label: item, kind: monaco.languages.CompletionItemKind.Function,
                    insertText: item, range, detail: "numpy",
                }));
                return {suggestions};
            }

            // Complétion après "pd."
            if (textBefore.endsWith("pd.")) {
                const pdItems = [
                    "DataFrame", "Series", "read_csv", "read_json", "read_excel",
                    "concat", "merge", "pivot_table", "crosstab", "cut", "qcut",
                    "to_datetime", "to_numeric", "isna", "notna", "NA", "NaT",
                ];
                pdItems.forEach(item => suggestions.push({
                    label: item, kind: monaco.languages.CompletionItemKind.Function,
                    insertText: item, range, detail: "pandas",
                }));
                return {suggestions};
            }

            // Complétion après "params."
            if (textBefore.endsWith("params.")) {
                ["get", "keys", "values", "items", "pop", "setdefault", "update"].forEach(item =>
                    suggestions.push({
                        label: item, kind: monaco.languages.CompletionItemKind.Method,
                        insertText: item === "get" ? 'get("${1:key}", ${2:None})' : item,
                        insertTextRules: item === "get" ? monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet : undefined,
                        range, detail: "dict",
                    })
                );
                return {suggestions};
            }

            // Snippets et mots-clés Python généraux
            const pyKeywords = [
                {label: "def", insertText: "def ${1:name}(${2:args}):\n    ${0}", detail: "function"},
                {label: "for", insertText: "for ${1:item} in ${2:iterable}:\n    ${0}", detail: "loop"},
                {label: "if", insertText: "if ${1:condition}:\n    ${0}", detail: "condition"},
                {label: "elif", insertText: "elif ${1:condition}:\n    ${0}", detail: "condition"},
                {label: "else", insertText: "else:\n    ${0}", detail: "condition"},
                {label: "while", insertText: "while ${1:condition}:\n    ${0}", detail: "loop"},
                {label: "try", insertText: "try:\n    ${1}\nexcept ${2:Exception} as e:\n    ${0}", detail: "exception"},
                {label: "with", insertText: "with ${1:expr} as ${2:var}:\n    ${0}", detail: "context"},
                {label: "class", insertText: "class ${1:Name}:\n    def __init__(self${2:, args}):\n        ${0}", detail: "class"},
                {label: "lambda", insertText: "lambda ${1:x}: ${0}", detail: "lambda"},
                {label: "return", insertText: "return ${0}", detail: "keyword"},
                {label: "import", insertText: "import ${0}", detail: "import"},
                {label: "from", insertText: "from ${1:module} import ${0}", detail: "import"},
                {label: "print", insertText: 'print(${0})', detail: "builtin"},
                {label: "len", insertText: "len(${0})", detail: "builtin"},
                {label: "range", insertText: "range(${0})", detail: "builtin"},
                {label: "enumerate", insertText: "enumerate(${0})", detail: "builtin"},
                {label: "zip", insertText: "zip(${0})", detail: "builtin"},
                {label: "isinstance", insertText: "isinstance(${1:obj}, ${2:type})", detail: "builtin"},
                {label: "list", insertText: "list(${0})", detail: "builtin"},
                {label: "dict", insertText: "dict(${0})", detail: "builtin"},
                {label: "set", insertText: "set(${0})", detail: "builtin"},
                {label: "tuple", insertText: "tuple(${0})", detail: "builtin"},
                {label: "sorted", insertText: "sorted(${0})", detail: "builtin"},
                {label: "map", insertText: "map(${1:func}, ${2:iterable})", detail: "builtin"},
                {label: "filter", insertText: "filter(${1:func}, ${2:iterable})", detail: "builtin"},
            ];
            pyKeywords.forEach(kw => suggestions.push({
                label: kw.label,
                kind: kw.detail === "keyword" || kw.detail === "condition" || kw.detail === "loop"
                    ? monaco.languages.CompletionItemKind.Keyword
                    : monaco.languages.CompletionItemKind.Snippet,
                insertText: kw.insertText,
                insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                range, detail: kw.detail,
            }));

            // Complétion sklearn courante
            const sklearnImports = [
                {label: "KMeans", text: "from sklearn.cluster import KMeans", detail: "sklearn.cluster"},
                {label: "DBSCAN", text: "from sklearn.cluster import DBSCAN", detail: "sklearn.cluster"},
                {label: "silhouette_score", text: "from sklearn.metrics import silhouette_score", detail: "sklearn.metrics"},
                {label: "StandardScaler", text: "from sklearn.preprocessing import StandardScaler", detail: "sklearn.preprocessing"},
                {label: "MinMaxScaler", text: "from sklearn.preprocessing import MinMaxScaler", detail: "sklearn.preprocessing"},
                {label: "LinearRegression", text: "from sklearn.linear_model import LinearRegression", detail: "sklearn.linear_model"},
                {label: "train_test_split", text: "from sklearn.model_selection import train_test_split", detail: "sklearn.model_selection"},
            ];
            sklearnImports.forEach(s => suggestions.push({
                label: s.label, kind: monaco.languages.CompletionItemKind.Module,
                insertText: s.text, range, detail: s.detail,
            }));

            return {suggestions};
        },
    });
}

// ── Autocomplétion R ───────────────────────────────────────────────────────
function registerRCompletion(monaco) {
    monaco.languages.registerCompletionItemProvider("r", {
        triggerCharacters: ["(", ",", " "],
        provideCompletionItems: (model, position) => {
            const word = model.getWordUntilPosition(position);
            const range = {
                startLineNumber: position.lineNumber,
                endLineNumber: position.lineNumber,
                startColumn: word.startColumn,
                endColumn: word.endColumn,
            };

            const suggestions = [];
            const rItems = [
                {label: "function", insertText: "function(${1:args}) {\n    ${0}\n}", detail: "keyword"},
                {label: "if", insertText: "if (${1:condition}) {\n    ${0}\n}", detail: "keyword"},
                {label: "else", insertText: "else {\n    ${0}\n}", detail: "keyword"},
                {label: "for", insertText: "for (${1:i} in ${2:seq}) {\n    ${0}\n}", detail: "keyword"},
                {label: "while", insertText: "while (${1:condition}) {\n    ${0}\n}", detail: "keyword"},
                {label: "tryCatch", insertText: "tryCatch({\n    ${1}\n}, error = function(e) {\n    ${0}\n})", detail: "error handling"},
                {label: "library", insertText: "library(${0})", detail: "import"},
                {label: "list", insertText: "list(${0})", detail: "data structure"},
                {label: "c", insertText: "c(${0})", detail: "combine"},
                {label: "data.frame", insertText: "data.frame(${0})", detail: "data structure"},
                {label: "lapply", insertText: "lapply(${1:X}, ${2:FUN})", detail: "apply"},
                {label: "sapply", insertText: "sapply(${1:X}, ${2:FUN})", detail: "apply"},
                {label: "mean", insertText: "mean(${0})", detail: "stats"},
                {label: "sd", insertText: "sd(${0})", detail: "stats"},
                {label: "var", insertText: "var(${0})", detail: "stats"},
                {label: "median", insertText: "median(${0})", detail: "stats"},
                {label: "sum", insertText: "sum(${0})", detail: "stats"},
                {label: "length", insertText: "length(${0})", detail: "base"},
                {label: "nrow", insertText: "nrow(${0})", detail: "base"},
                {label: "ncol", insertText: "ncol(${0})", detail: "base"},
                {label: "paste", insertText: "paste(${0})", detail: "string"},
                {label: "paste0", insertText: "paste0(${0})", detail: "string"},
                {label: "sprintf", insertText: 'sprintf("${1:%s}", ${0})', detail: "string"},
                {label: "is.null", insertText: "is.null(${0})", detail: "test"},
                {label: "is.na", insertText: "is.na(${0})", detail: "test"},
                {label: "as.integer", insertText: "as.integer(${0})", detail: "cast"},
                {label: "as.numeric", insertText: "as.numeric(${0})", detail: "cast"},
                {label: "as.character", insertText: "as.character(${0})", detail: "cast"},
                {label: "as.logical", insertText: "as.logical(${0})", detail: "cast"},
            ];
            rItems.forEach(item => suggestions.push({
                label: item.label,
                kind: item.detail === "keyword"
                    ? monaco.languages.CompletionItemKind.Keyword
                    : monaco.languages.CompletionItemKind.Function,
                insertText: item.insertText,
                insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                range, detail: item.detail,
            }));
            return {suggestions};
        },
    });
}
